<template>
  <div class="essays-view w-100">
    <essays-table
      :key="folderId"
      class="w-100"
      :essays="essays"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :table-loading="tableLoading"
      :initial-loading="initialLoading"
      :total="+count"
      :limit="+limit"
      :filters="{ ...filters, ...defaultFilters }"
      :show-pagination="true"
      :is-filtered="isFiltered"
      :current-page="+currentPage"
      :selected-all="selectedAll"
      :is-native="!isLMS"
      @move="showMoveEssayModal"
      @delete="deleteEssays"
      @getData="fetchEssays($event).then(startPolling)"
      @selectedAll="selectedAll = $event"
      @onChangePage="
        (currentPage = $event) && fetchEssays($event).then(startPolling)
      "
    />

    <move-essay-modal
      :essay-id="essayToMoveId"
      @essayMoved="setMoveSuccess"
      @moveError="setMoveError"
    />
  </div>
</template>

<script>
import EssaysTable from "../components/essay/essays_table.vue";
import MoveEssayModal from "../components/essay/modals/move_essay_modal.vue";
import fetchEssays from "@/mixins/fetchEssays";
import isLMS from "@/mixins/isLMS";
import { deleteEssay } from "@/api";
import Visibility from "visibilityjs";
import {
  generateFilterName,
  getFiltersFromLocalStorage,
} from "@/utils/localStorage";
import { createPolling } from "@/utils/polling";

export default {
  name: "EssaysView",
  components: {
    EssaysTable,
    MoveEssayModal,
  },

  mixins: [fetchEssays, isLMS],
  props: {
    isMenuOpen: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      error: "",
      polling: null,
      essayToMoveId: null,
      essayToDeleteId: null,
      reviewedCount: 0,
      gradedCount: 0,
      selectedAll: false,
      nextUpdateTime: null,
      gradeMoreOnboardingEmitted: false,
    };
  },

  computed: {
    queuedEssaysExist() {
      return this.essays.some((item) => item.status === "Queued");
    },

    folderId() {
      return this.$route.params.id;
    },
  },
  watch: {
    essays: {
      handler(essays) {
        if (!this.folderId || this.gradeMoreOnboardingEmitted) return;
        const hasGraded = essays.some((e) =>
          ["Graded", "Reviewed", "Exported"].includes(e.status)
        );
        if (hasGraded) {
          this.gradeMoreOnboardingEmitted = true;
          this.$eventBus.$emit("onboarding:graded:gradeMoreSubmissionsButton");
        }
      },
      immediate: true,
    },
    currentPage() {
      if (this.currentPage > 1) {
        this.stopPolling();
      } else if (this.queuedEssaysExist) {
        this.startPolling();
      }
    },
    async folderId(newFolderId) {
      this.gradeMoreOnboardingEmitted = false;
      this.stopPolling();
      this.filters = {};
      this.currentPage = 1;
      this.selectedAll = false;

      if (newFolderId) {
        this.defaultFilters = {
          folderId: newFolderId,
        };
      } else {
        this.defaultFilters = {};
      }

      await this.fetchEssays();

      if (this.queuedEssaysExist) {
        this.startPolling();
      }
    },
    queuedEssaysExist: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.initPolling();
        } else {
          this.stopPolling();
        }
        this.$eventBus.$emit("queued-essays-changed", newVal);
      },
    },
  },
  async beforeMount() {
    if (this.folderId) {
      this.defaultFilters = {
        folderId: this.folderId,
      };
    }

    const filterName = generateFilterName({
      entity: "assignments",
      id: this.$route.params.id,
    });
    const filters = getFiltersFromLocalStorage(filterName);

    let parsedFilters = null;
    if (filters) {
      try {
        parsedFilters = filters;

        // Migrate old reviewStatus field to status
        if (parsedFilters?.reviewStatus) {
          parsedFilters.status = parsedFilters.reviewStatus;
          delete parsedFilters.reviewStatus;
        }
      } catch (error) {
        console.error("Failed to parse filters from storage:", error);
      }
    }
    // Initial fetch
    await this.fetchEssays({
      filters: parsedFilters,
    });

    // Stop polling when the tab is inactive
    // Start polling again when the tab is active.
    Visibility.change(() => {
      if (Visibility.hidden()) {
        this.stopPolling();
      } else if (this.queuedEssaysExist) {
        this.startPolling();
      }
    });
  },
  mounted() {
    // user was re-routed after deleting an essay
    if (this.$route.query.deleted) {
      this.showDeleteSuccess();
    }

    if (this.$route.query.moved) {
      this.showMoveSuccess();
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    initPolling() {
      if (this.polling) {
        this.polling.stop();
      }

      this.polling = createPolling({
        callback: this.fetchEssays,
        interval: 20000,
        immediate: false,
        validate: () => this.queuedEssaysExist,
      });

      if (this.queuedEssaysExist) {
        this.polling.start();
      }
    },

    async deleteEssays(essaysIds) {
      try {
        await deleteEssay(this.selectedAll ? [] : essaysIds, this.filters);
        this.showDeleteSuccess();
        await this.fetchEssays();
      } catch (error) {
        this.error = error.response?.data?.error;
      }
    },
    showMoveEssayModal(essayId) {
      this.essayToMoveId = essayId;
      this.$bvModal.show("move-essay-modal");
    },

    setMoveSuccess() {
      this.fetchEssays();
      this.showMoveSuccess();
    },
    setMoveError(error) {
      this.error = error;
    },

    startPolling() {
      if (this.polling && !this.polling.isPolling) {
        this.polling.start();
      }
    },

    stopPolling() {
      if (this.polling) {
        this.polling.stop();
      }
    },

    handlePollingError(error) {
      console.error("Polling error:", error);
    },
    showMoveSuccess() {
      this.$showToastSuccess(this.$t("essays.essayMovedSuccessfully"), {
        title: this.$t("common.success"),
        autoHideDelay: 3000,
      });
    },
    showDeleteSuccess() {
      this.$showToastSuccess(this.$t("essays.essaysDeleted"), {
        title: this.$t("essays.essaysDeletedTitle"),
        autoHideDelay: 3000,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.essays-view {
  position: relative;
  display: flex;
  width: 100%;
  max-width: 100%;
  flex-direction: column;
  border-top: none;
  &::before {
    content: "";
    position: sticky;
    top: -24px;
    left: 0;
    width: 100%;
    height: 36px;
    background: hsl(210deg, 29%, 97%);
    z-index: 2;
  }
}

.title {
  font-size: 32px;
}

.is-menu-closed {
  margin-left: 235px;
}
</style>
